import { watch } from 'vue';
import { useColorMode } from '@vueuse/core';

/**
 * Composable to sync Frontegg Admin Portal theme with app's color mode
 * Switches between Frontegg's built-in 'dark' and 'modern' themes
 * @returns {Object} Object containing theme synchronization functions
 */
export function useFronteggTheme() {
    const mode = useColorMode();

    /**
     * Update Frontegg app configuration with new theme
     * Since CSS can't penetrate iframes, we need to update the Frontegg app instance
     */
    const updateFronteggTheme = async (isDark) => {
        console.log(`Updating Frontegg theme: ${isDark ? 'dark' : 'light'}`);
        
        try {
            // Try to access the Frontegg app instance and update its theme options
            const { AppHolder } = await import('@frontegg/js');
            const app = AppHolder.getInstance('default');
            
            if (app && app.options) {
                const themeName = isDark ? 'dark' : 'modern';
                console.log(`Updating app theme options to: ${themeName}`);
                
                // Update the theme options in the app instance
                app.options.themeOptions = {
                    adminPortal: {
                        themeName
                    }
                };
                
                // Try to update metadata to force theme refresh
                if (app.updateMetadata) {
                    app.updateMetadata(app.options.metadata);
                }
                
                console.log('Frontegg theme options updated successfully');
                
                // Check if admin portal is currently open and refresh it
                const adminPortalElement = document.querySelector('iframe[src*="frontegg"], [data-frontegg-app="true"]');
                if (adminPortalElement) {
                    console.log('Admin portal is open, attempting to refresh with new theme...');
                    
                    // Try to close and reopen the admin portal
                    try {
                        const { AdminPortal } = await import('@frontegg/vue');
                        
                        // Close current portal
                        AdminPortal.hide();
                        
                        // Reopen with new theme after short delay
                        setTimeout(() => {
                            AdminPortal.show();
                            console.log('Admin portal reopened with new theme');
                        }, 100);
                        
                    } catch (portalError) {
                        console.warn('Could not refresh admin portal automatically:', portalError);
                        console.warn('Please close and reopen the admin portal to see theme change');
                    }
                } else {
                    console.log('Admin portal not currently open - theme will apply when opened');
                }
                
            } else {
                console.warn('Frontegg app instance not found');
            }
        } catch (error) {
            console.error('Failed to update Frontegg theme:', error);
            
            // Fallback: Store theme preference for next portal open
            localStorage.setItem('frontegg-theme-preference', isDark ? 'dark' : 'light');
            console.log('Stored theme preference for next portal open');
        }
    };

    /**
     * Initialize theme synchronization
     */
    const initializeTheme = () => {
        console.log('Initializing Frontegg theme sync');

        // Watch for color mode changes
        watch(mode, (newMode) => {
            console.log('Color mode changed to:', newMode);
            updateFronteggTheme(newMode === 'dark');
        }, { immediate: false });

        // Test initial theme after delay
        setTimeout(() => {
            console.log('Testing initial theme setup...');
            updateFronteggTheme(mode.value === 'dark');
        }, 3000);
    };

    return {
        updateFronteggTheme,
        initializeTheme
    };
}